/**
 * CRM sidebar.
 *
 * Every item declares `role: [...]` — the SAME rule the main admin panel uses in
 * navigation/vertical/dashboard.js, filtered by VerticalNavMenuCRM against the
 * user's role + permissions from localStorage. An item with no `role` would be
 * hidden from everyone, so each one is explicit.
 *
 * The roles that exist:
 *   admin / super-manager / manager  — see everything
 *   office-manager                   — sees their office
 *   markter                          — SALESPERSON (the seeded role name); sees
 *                                      only what is assigned to him
 *   marketing / after-sales          — BRD §8.1 roles
 *
 * Hiding a link is presentation only — the real protection is server-side in
 * CrmAccess (lists are scoped) and in the controllers (Targets is manager-only and
 * returns 403). Never rely on this file for security.
 */

// Everyone who is allowed into the CRM at all.
const ALL_CRM = [
  'admin',
  'super-manager',
  'manager',
  'office-manager',
  'markter',
  'marketing',
  'after-sales',
]

// Management views: team-wide numbers and targets.
const MANAGERS = ['admin', 'super-manager', 'manager', 'office-manager', 'marketing']

// Karim, 2 Sep: "for the product and price, you remove this part. For the
// dashboard, you remove this part... no need for dashboard for the sales." A
// salesperson's day is My Day / Contacts / Companies / Leads / Pipeline /
// Quotations / Tasks — nothing else. marketing/after-sales are untouched (they
// were never mentioned, and after-sales genuinely has its own scoped dashboard
// view — BRD §8.1 "Retention Only").
const NOT_SALES = ALL_CRM.filter(role => role !== 'markter')

export default [
  {
    label: 'Overview',
    items: [
      {
        // The salesperson's own working list. Scoped to him server-side; a manager
        // can switch it to the whole team and filter assigned / unassigned.
        label: 'My Day',
        icon: 'SunriseIcon',
        route: 'crm-system-my-day',
        color: 'warning',
        role: ALL_CRM,
      },
      {
        // Karim, 2 Sep: "no need for dashboard for the sales" — a rep's numbers
        // live on My Day (his own count), not a dashboard.
        label: 'Dashboard',
        icon: 'GridIcon',
        route: 'crm-system-dashboard',
        color: 'primary',
        role: NOT_SALES,
      },
    ],
  },
  {
    label: 'Customers',
    items: [
      {
        label: 'Contacts',
        icon: 'UsersIcon',
        route: 'crm-system-contacts',
        color: 'info',
        role: ALL_CRM,
      },
      {
        label: 'Companies',
        icon: 'BriefcaseIcon',
        route: 'crm-system-companies',
        color: 'primary',
        role: ALL_CRM,
      },
      {
        label: 'Leads',
        icon: 'TrendingUpIcon',
        route: 'crm-system-leads',
        color: 'warning',
        role: ALL_CRM,
      },
    ],
  },
  {
    label: 'Sales',
    items: [
      {
        label: 'Opportunities',
        icon: 'BarChart2Icon',
        route: 'crm-system-pipeline',
        color: 'primary',
        role: ALL_CRM,
      },
      {
        label: 'Quotations',
        icon: 'FileTextIcon',
        route: 'crm-system-quotations',
        color: 'success',
        role: ALL_CRM,
      },
      {
        // Karim, 2 Sep: "for the product and price, you remove this part" — a rep
        // still picks a product and sees its price inline while building an
        // opportunity (that lookup stays); the standalone catalogue page does not.
        label: 'Products & Prices',
        icon: 'PackageIcon',
        route: 'crm-system-products',
        color: 'info',
        role: NOT_SALES,
      },
      {
        // Karim, 18 Sep: campaign management — create/edit/repeat campaigns,
        // see funnel + ROI. Browsing the picker itself (lead/opportunity
        // forms) is open to everyone; this management screen is not.
        label: 'Campaigns',
        icon: 'TrendingUpIcon',
        route: 'crm-system-campaigns',
        color: 'primary',
        role: NOT_SALES,
      },
    ],
  },
  {
    label: 'Activities',
    items: [
      {
        label: 'Tasks',
        icon: 'CheckSquareIcon',
        route: 'crm-system-tasks',
        color: 'warning',
        role: ALL_CRM,
      },
      // Calendar & Emails removed until the integrations exist (audit P1-1):
      // a dead link costs more trust than an absent feature.
    ],
  },
  {
    label: 'Insights',
    items: [
      {
        label: 'Reports',
        icon: 'PieChartIcon',
        route: 'crm-system-dashboard',
        color: 'success',
        role: MANAGERS,
      },
      {
        // Karim, 22 Sep: "for markter they care about client and opportunity
        // and help the ce to get the needs from the client in crm so in crm
        // page the main is the client with request open to see." This used
        // to just route to crm-system-dashboard — a dead duplicate of
        // Reports above, not a real feature. ALL_CRM (not MANAGERS): a
        // marketer needs this page to do their actual job, not just their
        // manager.
        label: 'Ongoing Requests',
        icon: 'ActivityIcon',
        route: 'crm-system-ongoing-requests',
        color: 'primary',
        role: ALL_CRM,
      },
      {
        // MID-CRM D1/D2/D3 — annual target vs actual per technical regulation.
        // The endpoint is manager-only (403 otherwise), so showing it to a
        // salesperson would only ever produce an error page.
        label: 'Targets & Progress',
        icon: 'TargetIcon',
        route: 'crm-system-targets',
        color: 'success',
        role: MANAGERS,
      },
    ],
  },
]
